import type { NormPoint } from '../core/types';
import type { ColoringEngine } from '../core/engine';

/**
 * Pointer input adapter.
 *
 * Translates raw Pointer Events on the drawing surface into engine calls, and
 * enforces the P0A touch rules:
 *  - exactly ONE active pointer at a time (secondary fingers are ignored)
 *  - the whole stroke stays locked to the region under the initial point
 *    (region locking itself is enforced by the engine)
 *  - pointercancel / lost capture safely aborts the stroke
 *  - browser gestures (scroll, zoom, double-tap, context menu, pull-to-refresh)
 *    are suppressed so they never interrupt drawing
 *
 * The mapping from client pixels to normalized artwork space is injected as
 * `toNormalized` so this adapter stays free of Phaser/layout specifics.
 */
export interface PointerInputOptions {
  target: HTMLElement;
  engine: ColoringEngine;
  toNormalized: (clientX: number, clientY: number) => NormPoint;
  /** Optional hook for debug overlay: latest raw + normalized pointer. */
  onPointer?: (info: { clientX: number; clientY: number; norm: NormPoint }) => void;
}

export class PointerInputAdapter {
  private activePointerId: number | null = null;
  private attached = false;

  constructor(private readonly opts: PointerInputOptions) {}

  attach(): void {
    if (this.attached) return;
    const t = this.opts.target;
    t.addEventListener('pointerdown', this.onDown);
    t.addEventListener('pointermove', this.onMove);
    t.addEventListener('pointerup', this.onUp);
    t.addEventListener('pointercancel', this.onCancel);
    t.addEventListener('lostpointercapture', this.onLostCapture);
    // Belt-and-braces gesture suppression on top of CSS `touch-action: none`.
    t.addEventListener('contextmenu', this.prevent);
    t.addEventListener('dblclick', this.prevent);
    t.addEventListener('gesturestart', this.prevent as EventListener);
    t.addEventListener('gesturechange', this.prevent as EventListener);
    t.addEventListener('touchmove', this.prevent, { passive: false });
    this.attached = true;
  }

  detach(): void {
    if (!this.attached) return;
    const t = this.opts.target;
    t.removeEventListener('pointerdown', this.onDown);
    t.removeEventListener('pointermove', this.onMove);
    t.removeEventListener('pointerup', this.onUp);
    t.removeEventListener('pointercancel', this.onCancel);
    t.removeEventListener('lostpointercapture', this.onLostCapture);
    t.removeEventListener('contextmenu', this.prevent);
    t.removeEventListener('dblclick', this.prevent);
    t.removeEventListener('gesturestart', this.prevent as EventListener);
    t.removeEventListener('gesturechange', this.prevent as EventListener);
    t.removeEventListener('touchmove', this.prevent);
    this.attached = false;
    this.activePointerId = null;
  }

  private prevent = (e: Event): void => {
    e.preventDefault();
  };

  private resolve(e: PointerEvent): NormPoint {
    const norm = this.opts.toNormalized(e.clientX, e.clientY);
    this.opts.onPointer?.({ clientX: e.clientX, clientY: e.clientY, norm });
    return norm;
  }

  private onDown = (e: PointerEvent): void => {
    // Ignore secondary touches while a stroke is active (no duplicate strokes).
    if (this.activePointerId !== null) {
      e.preventDefault();
      return;
    }
    const norm = this.resolve(e);
    const region = this.opts.engine.beginStroke(norm);
    if (region === null) {
      // Outside the turtle: do not start a stroke, do not capture.
      return;
    }
    e.preventDefault();
    this.activePointerId = e.pointerId;
    try {
      this.opts.target.setPointerCapture(e.pointerId);
    } catch {
      /* capture may be unavailable; stroke still works via move/up */
    }
  };

  private onMove = (e: PointerEvent): void => {
    if (e.pointerId !== this.activePointerId) return;
    e.preventDefault();
    // coalesced events give smoother fast strokes where supported
    const events = typeof e.getCoalescedEvents === 'function' ? e.getCoalescedEvents() : [e];
    for (const ev of events.length ? events : [e]) {
      this.opts.engine.extendStroke(this.resolve(ev));
    }
  };

  private onUp = (e: PointerEvent): void => {
    if (e.pointerId !== this.activePointerId) return;
    e.preventDefault();
    this.opts.engine.extendStroke(this.resolve(e));
    this.opts.engine.endStroke();
    this.release(e.pointerId);
  };

  private onCancel = (e: PointerEvent): void => {
    if (e.pointerId !== this.activePointerId) return;
    this.opts.engine.cancelStroke();
    this.release(e.pointerId);
  };

  private onLostCapture = (e: PointerEvent): void => {
    if (e.pointerId !== this.activePointerId) return;
    // Capture was lost mid-stroke: commit what we have rather than lose it.
    this.opts.engine.endStroke();
    this.activePointerId = null;
  };

  private release(pointerId: number): void {
    try {
      this.opts.target.releasePointerCapture(pointerId);
    } catch {
      /* ignore */
    }
    this.activePointerId = null;
  }
}
